import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, FileQuestion, Plus, Save } from "lucide-react";
import apiClient from "../../utils/apiClient";
import {
  AppButton, AppCard, AppCardContent, AppCardHeader, AppCardTitle, Callout, FormActionBar,
  PageHeader, StepRail, cn,
} from "../ui";
import Shimmer from "../Common/Shimmer";
import CommonToaster from "../Common/CommonToaster";
import QuizCategoryFormModal from "./QuizCategoryFormModal";
import PassagePickerModal from "./PassagePickerModal";
import { usePlan } from "../../contexts/PlanContext";
import TopicStep from "./questionEditor/TopicStep";
import TypeStep from "./questionEditor/TypeStep";
import QuestionStep from "./questionEditor/QuestionStep";
import AnswerStep from "./questionEditor/AnswerStep";
import MarksStep from "./questionEditor/MarksStep";
import ReviewStep from "./questionEditor/ReviewStep";
import QuestionPreview from "./questionEditor/QuestionPreview";
import {
  STEPS, allProblems, buildPayload, changeType, emptyForm, fieldErrors, firstProblemStep,
  formFromQuestion, nextQuestion, stepIndex, stepProblems, typesFor,
} from "./questionEditor/questionRules";
import { bankBase, folderPath, isLibraryPath, parentOf, questionTrail } from "./quizCenterTrail";

/** What each step asks of the author, in a sentence. */
const STEP_COPY = {
  topic: {
    title: "Choose the topic",
    intro: "Where this question lives in your bank. Assessments find questions by their topic.",
  },
  type: {
    title: "Choose the question type",
    intro: "How students answer it. The next steps ask only for what this type needs.",
  },
  question: {
    title: "Write the question",
    intro: "What students read — with a picture, a code snippet or a passage if it needs one.",
  },
  answer: {
    title: "Set the answer",
    intro: "What counts as correct. Students see it only after they submit, and only if the assessment shows answers.",
  },
  marks: {
    title: "Marks and notes",
    intro: "What it is worth and what students read afterwards. Everything here already has a default.",
  },
  review: {
    title: "Review and save",
    intro: "Check it once as students will see it, then save it to the bank.",
  },
};

const PREVIEW_STEPS = ["question", "answer", "marks"];
const LAST = STEPS.length - 1;

/** A keyboard key in the save bar's shortcut hint. */
const Kbd = ({ children }) => (
  <kbd className="tw-mx-[0.15rem] tw-inline-block tw-rounded-[0.25rem] tw-border tw-border-solid tw-border-line tw-bg-muted tw-px-[0.35rem] tw-py-[0.1rem] tw-font-mono tw-text-[0.7rem] tw-text-content">
    {children}
  </kbd>
);

/**
 * AddEditQuizQuestionPage — write one question for the bank, a step at a time:
 * Topic → Type → Question → Answer → Marks & notes → Review.
 * `/quiz-bank/add` · `/quiz-bank/edit/:id`, and the platform library's
 * `/sa/marketplace/quiz-questions/add|edit/:id`.
 *
 * Every rule is in questionEditor/questionRules.js; this page holds the form,
 * walks the steps and talks to the API. A step's problems show once the author
 * tries to move on. Opened from a folder (`?category=`), the topic is already
 * chosen and the walk starts at Type; an edit opens on Review.
 */
const AddEditQuizQuestionPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const isLibrary = isLibraryPath(useLocation().pathname);
  const base = bankBase(isLibrary);
  const [searchParams] = useSearchParams();
  const { hasPlanFeature } = usePlan();

  // "Code reading" is coding: hidden from institute authors when Coding Practice
  // is off. The platform library always keeps it.
  const types = typesFor({ coding: isLibrary || hasPlanFeature("codingPractice") });

  const prefilledCategory = isEdit ? "" : searchParams.get("category") || "";
  const startAt = isEdit ? LAST : prefilledCategory ? stepIndex("type") : 0;

  const [form, setForm] = useState(() => (prefilledCategory
    ? { ...emptyForm(), category: prefilledCategory, subcategory: searchParams.get("subcategory") || "" }
    : emptyForm()));
  const [step, setStep] = useState(startAt);
  const [reached, setReached] = useState(startAt);
  const [attempted, setAttempted] = useState({});
  const [loadState, setLoadState] = useState(isEdit ? "loading" : "ready");
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  // A category made from here opens a dialog rather than leaving the page, so a
  // half-written question is not lost to make room for its topic.
  const [categoryFor, setCategoryFor] = useState(null); // { parentId } while open
  // The passage's title and kind, for its card — the form holds only its id.
  const [passageInfo, setPassageInfo] = useState(null);
  const [passagePickerOpen, setPassagePickerOpen] = useState(false);
  // Opened from a passage's page: it sits under that passage, and goes back
  // there after saving.
  const fromPassage = searchParams.get("from") === "passage";
  // Where the question lives now — the folder it was started from, or the saved
  // question's — for the breadcrumbs. Not the topic being picked in the form:
  // back returns to where the author came from.
  const [home, setHome] = useState(() => ({
    category: prefilledCategory,
    subcategory: prefilledCategory ? searchParams.get("subcategory") || "" : "",
  }));

  const set = useCallback((key, value) => setForm((f) => ({ ...f, [key]: value })), []);

  useEffect(() => {
    apiClient.get("/quiz-categories?flat=true")
      .then(({ data }) => setCategories(data.categories || []))
      .catch(() => {});
  }, []);

  // "Add question" on a passage's page.
  useEffect(() => {
    if (isEdit) return;
    const pid = searchParams.get("passage");
    if (!pid) return;
    setForm((f) => ({ ...f, passage: pid }));
    apiClient.get(`/quiz-passages/${pid}`)
      .then(({ data }) => setPassageInfo(data.passage))
      .catch(() => {
        setForm((f) => ({ ...f, passage: "" }));
        setToast({ show: true, message: "That passage no longer exists.", type: "error" });
      });
  }, [isEdit, searchParams]);

  const load = useCallback(() => {
    if (!isEdit) return;
    setLoadState("loading");
    apiClient.get(`/quiz-questions/${id}`)
      .then(({ data }) => {
        const q = data.question;
        const loaded = formFromQuestion(q);
        setForm(loaded);
        setHome({ category: loaded.category, subcategory: loaded.subcategory });
        setPassageInfo(q.passage?._id ? q.passage : null);
        setLoadState("ready");
      })
      .catch(() => setLoadState("error"));
  }, [id, isEdit]);

  useEffect(() => { load(); }, [load]);

  const trail = questionTrail({
    isLibrary,
    isEdit,
    categories,
    ...home,
    passage: fromPassage ? passageInfo || (form.passage ? { _id: form.passage } : null) : null,
  });
  const backTo = parentOf(trail);
  // After a save: the passage it came from, or the folder it is now in.
  const savedTo = () => (fromPassage && form.passage
    ? `${base}/passages/${form.passage}`
    : folderPath(base, form.category, form.subcategory));

  // ── steps ──────────────────────────────────────────────────────────
  const stepKey = STEPS[step].key;
  const liveProblems = stepProblems(form, stepKey);
  const errors = fieldErrors(attempted[stepKey] ? liveProblems : []);
  const problems = useMemo(() => allProblems(form), [form]);
  const flagged = STEPS
    .filter((s, i) => i < reached && i !== step && s.key !== "review" && stepProblems(form, s.key).length > 0)
    .map((s) => s.key);

  const goTo = (index) => {
    const next = Math.max(0, Math.min(index, LAST));
    setStep(next);
    setReached((r) => Math.max(r, next));
    window.scrollTo?.({ top: 0, behavior: "smooth" });
  };
  const goToKey = (key) => goTo(stepIndex(key));

  const next = () => {
    setAttempted((a) => ({ ...a, [stepKey]: true }));
    if (liveProblems.length) {
      setTimeout(() => document.querySelector('[aria-invalid="true"]')?.focus?.(), 0);
      return;
    }
    goTo(step + 1);
  };

  // ── save ───────────────────────────────────────────────────────────
  const save = async ({ another = false } = {}) => {
    if (saving) return;
    const first = firstProblemStep(form);
    if (first) {
      // Show every step's problems, and open the first one that has any.
      setAttempted(Object.fromEntries(STEPS.map((s) => [s.key, true])));
      goToKey(first);
      setToast({ show: true, message: problems[0].message, type: "error" });
      return;
    }
    setSaving(true);
    try {
      if (isEdit) await apiClient.put(`/quiz-questions/${id}`, buildPayload(form));
      else await apiClient.post("/quiz-questions", buildPayload(form));
      if (another) {
        setToast({ show: true, message: "Saved — now the next one", type: "success" });
        setForm(nextQuestion(form));
        setAttempted({});
        goToKey("question");
      } else {
        setToast({ show: true, message: isEdit ? "Question updated" : "Question created", type: "success" });
        setTimeout(() => navigate(savedTo()), 450);
      }
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Failed to save question", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  // Ctrl+S saves from any step — not while a dialog is open, where it means
  // something else.
  const saveRef = useRef(save);
  saveRef.current = save;
  const dialogOpen = passagePickerOpen || !!categoryFor;
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        if (!dialogOpen) saveRef.current();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dialogOpen]);

  // ── render ─────────────────────────────────────────────────────────
  const header = (
    <PageHeader
      icon={FileQuestion}
      title={isEdit ? "Edit question" : "New question"}
      description={isEdit
        ? "Students who have already answered it keep the version they saw."
        : "A few guided steps. It goes into your question bank when you save."}
      breadcrumbs={trail}
    />
  );

  if (loadState === "loading") {
    return (
      <div className="tw-flex tw-flex-col tw-gap-4" aria-busy="true" aria-label="Loading question">
        {header}
        <div className="tw-grid tw-gap-6 min-[992px]:tw-grid-cols-[15rem_minmax(0,1fr)]">
          <Shimmer height={320} />
          <Shimmer height={480} />
        </div>
      </div>
    );
  }
  if (loadState === "error") {
    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        {header}
        <Callout tone="danger" role="alert" action={<AppButton size="xs" variant="outline" onClick={load}>Try again</AppButton>}>
          The question could not be loaded. It may have been deleted.
        </Callout>
      </div>
    );
  }

  const copy = STEP_COPY[stepKey];
  const isReview = stepKey === "review";
  const showPreview = PREVIEW_STEPS.includes(stepKey);

  const stepBody = {
    topic: (
      <TopicStep
        form={form}
        set={set}
        setForm={setForm}
        categories={categories}
        errors={errors}
        isLibrary={isLibrary}
        onNewCategory={(parentId) => setCategoryFor({ parentId })}
      />
    ),
    type: <TypeStep value={form.questionType} types={types} onChange={(t) => setForm((f) => changeType(f, t))} />,
    question: (
      <QuestionStep
        form={form}
        set={set}
        errors={errors}
        passage={passageInfo}
        onPickPassage={() => setPassagePickerOpen(true)}
        onRemovePassage={() => { set("passage", ""); setPassageInfo(null); }}
      />
    ),
    answer: <AnswerStep form={form} setForm={setForm} set={set} errors={errors} />,
    marks: <MarksStep form={form} set={set} setForm={setForm} errors={errors} />,
    review: (
      <ReviewStep
        form={form}
        categories={categories}
        passage={passageInfo}
        problems={problems}
        onGoTo={goToKey}
        isLibrary={isLibrary}
      />
    ),
  }[stepKey];

  return (
    <div className="has-form-actionbar tw-flex tw-flex-col tw-gap-4">
      {header}

      <div
        className={cn(
          "tw-grid tw-items-start tw-gap-6 min-[992px]:tw-grid-cols-[15rem_minmax(0,1fr)]",
          showPreview && "min-[1200px]:tw-grid-cols-[15rem_minmax(0,1fr)_20rem]",
        )}
      >
        <div className="min-[992px]:tw-sticky min-[992px]:tw-top-4">
          <StepRail steps={STEPS} current={step} reached={reached} onSelect={goTo} flagged={flagged} />
        </div>

        <section
          aria-labelledby="qe-step-title"
          className={cn("tw-flex tw-min-w-0 tw-flex-col tw-gap-4", !showPreview && "tw-max-w-4xl")}
        >
          <div className="tw-flex tw-flex-col tw-gap-1">
            <h2 id="qe-step-title" className="tw-m-0 tw-text-[1.375rem] tw-font-semibold tw-tracking-tight tw-text-content">
              {copy.title}
            </h2>
            <p className="tw-m-0 tw-text-sm tw-text-content-muted">{copy.intro}</p>
          </div>
          {stepBody}
        </section>

        {showPreview && (
          <div className="tw-hidden min-[1200px]:tw-sticky min-[1200px]:tw-top-4 min-[1200px]:tw-block">
            <AppCard>
              <AppCardHeader>
                <AppCardTitle>How students see it</AppCardTitle>
              </AppCardHeader>
              <AppCardContent>
                <QuestionPreview form={form} passage={passageInfo} />
              </AppCardContent>
            </AppCard>
          </div>
        )}
      </div>

      <FormActionBar info={<span className="max-[767px]:tw-hidden"><Kbd>Ctrl</Kbd>+<Kbd>S</Kbd> to save</span>}>
        {step === 0 ? (
          <AppButton type="button" variant="outline" onClick={() => navigate(backTo)} disabled={saving}>Cancel</AppButton>
        ) : (
          <AppButton type="button" variant="outline" onClick={() => goTo(step - 1)} disabled={saving}>
            <ArrowLeft size={16} aria-hidden="true" /> Back
          </AppButton>
        )}
        {isReview ? (
          <>
            {!isEdit && (
              <AppButton type="button" variant="outline" onClick={() => save({ another: true })} disabled={saving}>
                <Plus size={16} aria-hidden="true" /> Save &amp; add another
              </AppButton>
            )}
            <AppButton type="button" onClick={() => save()} disabled={saving}>
              <Save size={16} aria-hidden="true" /> {saving ? "Saving…" : isEdit ? "Save changes" : "Create question"}
            </AppButton>
          </>
        ) : (
          <>
            {isEdit && (
              <AppButton type="button" variant="outline" onClick={() => save()} disabled={saving}>
                <Save size={16} aria-hidden="true" /> {saving ? "Saving…" : "Save changes"}
              </AppButton>
            )}
            <AppButton type="button" onClick={next} disabled={saving}>
              Continue <ArrowRight size={16} aria-hidden="true" />
            </AppButton>
          </>
        )}
      </FormActionBar>

      <PassagePickerModal
        show={passagePickerOpen}
        onHide={() => setPassagePickerOpen(false)}
        categories={categories}
        defaultCategory={form.category}
        onPick={(p) => {
          set("passage", String(p._id));
          setPassageInfo(p);
          setPassagePickerOpen(false);
        }}
      />

      <QuizCategoryFormModal
        show={!!categoryFor}
        onHide={() => setCategoryFor(null)}
        parentId={categoryFor?.parentId || null}
        isLibrary={isLibrary}
        onSaved={(saved) => {
          const parentId = categoryFor?.parentId || null;
          setCategoryFor(null);
          if (saved?._id) {
            setCategories((prev) => [...prev, { ...saved, parentCategory: saved.parentCategory || parentId }]);
            setForm((f) => (parentId
              ? { ...f, category: String(parentId), subcategory: String(saved._id) }
              : { ...f, category: String(saved._id), subcategory: "" }));
          }
          setToast({ show: true, message: parentId ? "Subcategory created" : "Category created", type: "success" });
        }}
      />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default AddEditQuizQuestionPage;
